import { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import * as Sentry from '@sentry/react';
import FileDragAndDropField from '../FileDragAndDropField';
import FileDisplay from '../FileDisplay';
import { BugReportForm } from './BugReportForm';

type Props = {
  open: boolean;
  onClose(): void;
};

const DEFAULT_VALUES = {
  email: '',
  title: '',
  overview: '',
  stepsToReproduce: '',
  attachments: [] as File[],
};

export const BugReport = ({ open, onClose }: Props) => {
  const [submitted, setSubmitted] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<BugReportForm>({
    resolver: classValidatorResolver(BugReportForm, {
      transformer: { targetMaps: [{ target: BugReportForm, properties: { attachments: Array } }] },
    }),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (open) {
      reset(DEFAULT_VALUES);
      setSubmitted(false);
    }
  }, [open, reset]);

  const attachmentsValue = watch('attachments');

  const onSubmit: SubmitHandler<BugReportForm> = async ({ email, title, overview, stepsToReproduce }) => {
    const attachments = attachmentsValue ?? [];
    if (!import.meta.env.VITE_SENTRY_DSN) {
      setError('root', { message: 'Sentry is not configured. Please contact support directly.' });
      return;
    }

    try {
      const sentryAttachments = await Promise.all(
        attachments.map(async (file) => {
          const buffer = await file.arrayBuffer();
          return { filename: file.name, data: new Uint8Array(buffer) };
        }),
      );

      const org = import.meta.env.VITE_SENTRY_ORG;
      const project = import.meta.env.VITE_SENTRY_PROJECT;

      const sentryLink =
        org && project
          ? `\n\nView in Sentry (includes attachments): https://sentry.io/organizations/${org}/feedback/?project=${project}${email ? `&query=user.email%3A${encodeURIComponent(email)}` : ''}`
          : '';

      const message = [
        `**Bug Title:** ${title}`,
        `\n**Bug Overview:**\n${overview}`,
        `\n**Steps To Reproduce:**\n${stepsToReproduce}`,
        sentryLink,
      ]
        .filter(Boolean)
        .join('\n');

      Sentry.captureFeedback({ email, message }, { attachments: sentryAttachments });

      setSubmitted(true);
    } catch {
      setError('root', { message: 'Failed to submit feedback. Please try again.' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Report a Bug</DialogTitle>

      <DialogContent>
        {submitted ? (
          <Alert severity="success">Thank you for your feedback!</Alert>
        ) : (
          <form
            id="report-bug-form"
            onSubmit={handleSubmit(onSubmit)}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            {errors.root && <Alert severity="error">{errors.root.message}</Alert>}

            <TextField
              {...register('email')}
              label="Email"
              type="email"
              fullWidth
              required
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
            />

            <TextField
              {...register('title')}
              label="Bug Title"
              placeholder="Enter the name of the bug."
              fullWidth
              required
              error={Boolean(errors.title)}
              helperText={errors.title?.message}
            />

            <TextField
              {...register('overview')}
              label="Bug Overview"
              placeholder="Provide an overview of the bug, including errors."
              multiline
              rows={4}
              fullWidth
              required
              error={Boolean(errors.overview)}
              helperText={errors.overview?.message}
            />

            <TextField
              {...register('stepsToReproduce')}
              label="Steps To Reproduce"
              placeholder="Please provide a list of steps to reproduce this bug, along with the browser version you are using."
              multiline
              rows={4}
              fullWidth
              required
              error={Boolean(errors.stepsToReproduce)}
              helperText={errors.stepsToReproduce?.message}
            />

            <Controller
              control={control}
              name="attachments"
              render={() => (
                <FileDragAndDropField
                  multiple
                  label="Attachments"
                  height={120}
                  showIcon={false}
                  error={errors.attachments?.message}
                  onDrop={(files) => {
                    setValue('attachments', [...files, ...(attachmentsValue ?? [])]);
                    clearErrors('attachments');
                  }}
                />
              )}
            />
            {(attachmentsValue ?? []).length > 0 && (
              <Box>
                <FileDisplay
                  files={Array.from(attachmentsValue ?? []).sort((a, b) => a.name.localeCompare(b.name))}
                  onRemove={(file: File) => {
                    setValue(
                      'attachments',
                      Array.from(attachmentsValue ?? []).filter((f) => f.name !== file.name),
                    );
                  }}
                />
              </Box>
            )}
          </form>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {submitted ? 'Close' : 'Cancel'}
        </Button>
        {!submitted && (
          <Button variant="contained" type="submit" form="report-bug-form" disabled={isSubmitting}>
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
